import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AdminService, PricingConfiguration, FAQConfiguration, FAQItem } from '../../core/services/admin.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, AfterViewInit {
  @ViewChild('heroVideo') heroVideo?: ElementRef<HTMLVideoElement>;
  videoPlaying = true; // Optimistisch, damit kein Button flackert

  useCases = [
    {
      title: 'Stadtfeste & Events',
      description: 'Live-Übertragungen, Programmhinweise und Sponsorenpräsentation für Festivals, Märkte und Stadtfeste',
      icon: '🎪'
    },
    {
      title: 'Firmenveranstaltungen',
      description: 'Präsentationen, Produktlaunches, Tagungen und Firmenfeiern mit professioneller Bildqualität',
      icon: '🏢'
    },
    {
      title: 'Messen & Kongresse',
      description: 'Messeauftritte, Kongressbegleitung und Ausstellungen mit hochwertiger Darstellung',
      icon: '🎯'
    },
    {
      title: 'Sportevents',
      description: 'Live-Ergebnisse, Replays und Sponsorenwerbung bei Sportveranstaltungen',
      icon: '⚽'
    },
    {
      title: 'Baustellenkommunikation',
      description: 'Projektvisualisierung, Baupläne und Informationen für Anwohner und Passanten',
      icon: '🏗️'
    },
    {
      title: 'Konzerte & Shows',
      description: 'Bühnenbegleitung, Live-Übertragungen und atmosphärische Visuals',
      icon: '🎵'
    }
  ];

  features = [
    {
      title: 'Hohe Auflösung',
      description: 'Pixel Pitch von 2,6mm für kristallklare Bilder',
      icon: '📺'
    },
    {
      title: 'Mobile Lösung',
      description: 'Komplette Videowand mit Rollenbasis, überall einsetzbar',
      icon: '🚚'
    },
    {
      title: 'Große Fläche',
      description: 'Großflächiges Display mit 2,5m x 1,5m Größe',
      icon: '📏'
    },
    {
      title: '360° Branding',
      description: 'Ihre Marke sichtbar – auch neben und hinter dem Screen',
      icon: '🎨'
    }
  ];

  trustFactors = [
    {
      title: 'Lokal in Leipzig',
      description: 'Schnelle Anlieferung, persönlicher Service und kurze Wege im Raum Leipzig',
      icon: '📍'
    },
    {
      title: 'Eigene Technik',
      description: 'Keine Vermittlung - wir vermieten unsere eigene professionelle Ausrüstung',
      icon: '🔧'
    },
    {
      title: 'Transparente Preise',
      description: 'Klare Kostenstruktur ohne versteckte Gebühren, Angebote innerhalb von 24h',
      icon: '💶'
    },
    {
      title: 'Persönlicher Ansprechpartner',
      description: 'Direkte Beratung und Support vor, während und nach Ihrer Veranstaltung',
      icon: '👤'
    }
  ];

  // Pricing Tiers - werden aus der DB geladen
  pricingTiers: Array<{
    name: string;
    pricePerDay: number;
    totalPrice: number;
    days: string;
    description: string;
    discount: number;
  }> = [];

  faqs: Array<FAQItem & { open: boolean }> = [];

  defaultFAQs: FAQItem[] = [
    {
      question: 'Wie weit im Voraus muss ich buchen?',
      answer: 'Wir empfehlen eine Buchung mindestens 2 Wochen im Voraus. Für kurzfristige Anfragen kontaktieren Sie uns bitte direkt - oft können wir auch spontane Buchungen realisieren.'
    },
    {
      question: 'Ist die Videowand wetterfest?',
      answer: 'Ja, unsere Videowand sind für den Outdoor-Einsatz konzipiert und können bei allen Wetterbedingungen eingesetzt werden - von Sonnenschein bis Regen.'
    },
    {
      question: 'Welchen Content kann ich abspielen?',
      answer: 'Sie können Videos, Bilder, Präsentationen, Live-Streams und dynamische Inhalte abspielen. Wir unterstützen alle gängigen Formate und beraten Sie gerne zur optimalen Aufbereitung.'
    },
    {
      question: 'Ist die Lieferung im Preis enthalten?',
      answer: 'Die Lieferung wird separat berechnet und hängt von der Entfernung ab. Für Standorte im Raum Leipzig (bis ca. 20 km) berechnen wir eine faire Pauschale. Bei der Buchung erhalten Sie sofort eine transparente Gesamtkalkulation inkl. aller Kosten.'
    },
    {
      question: 'Können Sie beim Aufbau helfen?',
      answer: 'Selbstverständlich! Unser Team übernimmt die komplette Lieferung, Aufstellung und Inbetriebnahme. Optional bieten wir auch technische Betreuung während Ihrer Veranstaltung an.'
    },
    {
      question: 'Gibt es Rabatte für längere Mietzeiten?',
      answer: 'Ja, bei Langzeitmieten (ab 7 Tagen) bieten wir attraktive Staffelpreise an. Sprechen Sie uns für ein individuelles Angebot an.'
    },
    {
      question: 'Kann die Videowand im Corporate Design gebrandet werden?',
      answer: 'Ja! Mit unserem 360° Branding können Sie Ihre Videowand komplett im Corporate Design branden – Seiten, untere Front und Rückseite. Das Branding ist wiederverwendbar für Folgetermine und macht Ihre Marke sichtbar aus allen Blickwinkeln. Voraussetzung: Buchung mindestens 14 Tage im Voraus. Express-Buchungen sind nach persönlicher Absprache möglich. Wir stellen die Grafikvorlagen bereit; die Grafikerstellung übernehmen Sie.'
    }
  ];

  constructor(private adminService: AdminService) {}

  ngOnInit(): void {
    // Standard-Werte direkt setzen (sofort sichtbar)
    this.setDefaultPricingTiers();
    this.loadDefaultFAQs();
    // Dann versuchen, Live-Daten zu laden
    this.loadPricingData();
    this.loadFAQData();
  }

  loadPricingData(): void {
    this.adminService.getPricingConfiguration().subscribe({
      next: (config) => {
        console.log('✅ Pricing-Konfiguration geladen:', config);
        this.calculatePricingTiers(config);
      },
      error: (error) => {
        console.error('❌ Fehler beim Laden der Pricing-Konfiguration:', error);
      }
    });
  }

  loadFAQData(): void {
    console.log('📚 Loading FAQ data from API...');
    this.adminService.getFAQConfiguration().subscribe({
      next: (config) => {
        console.log('✅ FAQ-Konfiguration geladen:', config);
        this.setFAQsFromConfig(config);
      },
      error: (error) => {
        console.error('❌ Fehler beim Laden der FAQ-Konfiguration:', error);
      }
    });
  }

  loadDefaultFAQs(): void {
    this.faqs = this.defaultFAQs.map(faq => ({
      ...faq,
      open: false
    }));
  }

  setFAQsFromConfig(config: FAQConfiguration): void {
    if (config && config.items && config.items.length > 0) {
      this.faqs = config.items.map(item => ({
        ...item,
        open: false
      }));
      console.log('✅ FAQs aktualisiert:', this.faqs);
    }
  }

  calculatePricingTiers(config: PricingConfiguration): void {
    const basePrice = config.rentalBasePrice;
    
    // Sortiere Tiers nach Tagen
    const sortedTiers = [...config.durationTiers].sort((a, b) => a.days - b.days);
    
    // Finde relevante Tiers für unsere 3 Kategorien
    const dayTier = sortedTiers.find(t => t.days === 1) || { days: 1, discount: 0 };
    const weekTier = sortedTiers.find(t => t.days === 7) || sortedTiers.find(t => t.days >= 5 && t.days <= 7) || { days: 7, discount: 0.30 };
    const longTier = sortedTiers.find(t => t.days === 14) || sortedTiers.find(t => t.days >= 14) || { days: 14, discount: 0.45 };
    
    this.pricingTiers = [
      {
        name: 'Tagesmiete',
        pricePerDay: Math.round(basePrice * (1 - dayTier.discount)),
        totalPrice: Math.round(basePrice * (1 - dayTier.discount) * 1),
        days: '1–3',
        description: 'Perfekt für einzelne Events und kurze Veranstaltungen (1–3 Tage)',
        discount: dayTier.discount * 100
      },
      {
        name: 'Wochenmiete',
        pricePerDay: Math.round(basePrice * (1 - weekTier.discount)),
        totalPrice: Math.round(basePrice * (1 - weekTier.discount) * weekTier.days),
        days: '5–7',
        description: 'Attraktive Staffelpreise ab 5–7 Tagen',
        discount: weekTier.discount * 100
      },
      {
        name: 'Langzeitmiete',
        pricePerDay: Math.round(basePrice * (1 - longTier.discount)),
        totalPrice: Math.round(basePrice * (1 - longTier.discount) * longTier.days),
        days: '14+',
        description: 'Sonderkonditionen für Dauer- und Projekteinsätze',
        discount: longTier.discount * 100
      }
    ];
  }

  setDefaultPricingTiers(): void {
    // Fallback-Werte falls API-Call fehlschlägt
    this.pricingTiers = [
      {
        name: 'Tagesmiete',
        pricePerDay: 490,
        totalPrice: 490,
        days: '1–3',
        description: 'Perfekt für einzelne Events und kurze Veranstaltungen (1–3 Tage)',
        discount: 0
      },
      {
        name: 'Wochenmiete',
        pricePerDay: 343,
        totalPrice: 2401,
        days: '5–7',
        description: 'Attraktive Staffelpreise ab 5–7 Tagen',
        discount: 30
      },
      {
        name: 'Langzeitmiete',
        pricePerDay: 270,
        totalPrice: 3773,
        days: '14+',
        description: 'Sonderkonditionen für Dauer- und Projekteinsätze',
        discount: 45
      }
    ];
  }

  toggleFaq(index: number): void {
    this.faqs[index].open = !this.faqs[index].open;
  }

  ngAfterViewInit(): void {
    // Kleiner Delay, dann Autoplay versuchen
    setTimeout(() => this.tryPlayHeroVideo(), 150);
  }

  tryPlayHeroVideo(): void {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;
    const playPromise = video.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch(() => {
        // Falls Autoplay blockiert, Button könnte gezeigt werden; hier halten wir silent
        this.videoPlaying = false;
      });
    }
  }

  scrollToUseCases(event: Event): void {
    event.preventDefault();
    const useCasesSection = document.getElementById('use-cases');
    if (useCasesSection) {
      useCasesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

}
